import { useCallback, useLayoutEffect, useRef, useState, type Ref } from 'react';

type NativeControl = HTMLInputElement | HTMLSelectElement;

/**
 * React tracks the last value it wrote to a control and drops change events that
 * do not move it. Writing through the prototype setter leaves that tracker stale,
 * so the dispatched event reaches onChange — and therefore react-hook-form — as if
 * the person had used the control directly.
 */
export function setNativeValue(el: NativeControl, value: string): void {
  const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el) as object, 'value')?.set;
  if (setter) setter.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

export function mergeRefs<T>(...refs: (Ref<T> | undefined)[]): (node: T | null) => void {
  return (node) => {
    for (const ref of refs) {
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as { current: T | null }).current = node;
    }
  };
}

export interface NativeControlBridge<T extends NativeControl> {
  ref: (node: T | null) => void;
  element: { current: T | null };
  value: string;
  commit: (next: string) => void;
}

/**
 * Keeps a rendered-by-us control in step with the real element behind it. The
 * element stays the source of truth, so a controlled `value` prop, a form reset
 * and a person clicking our own UI all converge on the same answer.
 */
export function useNativeControl<T extends NativeControl>(forwarded?: Ref<T>): NativeControlBridge<T> {
  const element = useRef<T | null>(null);
  const [value, setValue] = useState('');

  useLayoutEffect(() => {
    const el = element.current;
    if (el && el.value !== value) setValue(el.value);
  });

  const commit = useCallback((next: string) => {
    const el = element.current;
    if (!el || el.disabled) return;
    setNativeValue(el, next);
    setValue(next);
  }, []);

  return { ref: mergeRefs<T>(forwarded, element), element, value, commit };
}
